class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this._renderQueue = Promise.resolve();
    this._pendingState = null;

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    const isOpening = !this.classList.contains('active');
    if (isOpening) {
      this.scrollPosition = window.scrollY;
      document.body.classList.add('overflow-hidden');
      document.body.style.setProperty('position', 'fixed');
      document.body.style.setProperty('top', `-${this.scrollPosition}px`);
      document.body.style.setProperty('width', '100%');

      this.addEventListener(
        'transitionend',
        () => {
          const containerToTrapFocusOn = this.classList.contains('is-empty')
            ? this.querySelector('.drawer__inner-empty')
            : document.getElementById('CartDrawer');
          const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
          trapFocus(containerToTrapFocusOn, focusElement);
        },
        { once: true }
      );
    }

    requestAnimationFrame(() => {
      setTimeout(() => {
        this.classList.add('animate', 'active');
      }, 0);
    });
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    document.body.style.removeProperty('position');
    document.body.style.removeProperty('top');
    document.body.style.removeProperty('width');
    window.scrollTo(0, this.scrollPosition || 0);
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this._pendingState = parsedState;
    this._renderQueue = this._renderQueue.then(() => this._renderContents());
    return this._renderQueue;
  }

  async _renderContents() {
    const parsedState = this._pendingState;
    if (!parsedState) return null;

    this.classList.remove('is-empty');
    if (parsedState.id) this.productId = parsedState.id;

    const wasActive = this.classList.contains('active');

    if (parsedState.sections) {
      this.applySectionsFromState(parsedState);
    }

    let renderedCount = this.getRenderedItemCount();
    this.classList.toggle('is-empty', renderedCount === 0);

    if (renderedCount > 0) {
      await this.syncIfCartHasMoreItems(renderedCount);
      renderedCount = this.getRenderedItemCount();
      this.classList.toggle('is-empty', renderedCount === 0);
    } else if (parsedState.sections) {
      await new Promise((resolve) => setTimeout(resolve, 150));
      await this.refreshSectionsFromServer();
      renderedCount = this.getRenderedItemCount();
      this.classList.toggle('is-empty', renderedCount === 0);
    }

    await new Promise((resolve) => {
      setTimeout(() => {
        const overlay = this.querySelector('#CartDrawer-Overlay');
        if (overlay) overlay.addEventListener('click', this.close.bind(this));
        if (!wasActive) this.open();
        resolve();
      });
    });

    try {
      return await fetch(`${routes.cart_url}.js`, { credentials: 'include' }).then((response) => response.json());
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  async syncIfCartHasMoreItems(currentRendered) {
    for (let attempt = 0; attempt < 4; attempt++) {
      await new Promise((resolve) => setTimeout(resolve, 100 * (attempt + 1)));

      let cart;
      try {
        cart = await this.fetchCart();
      } catch (error) {
        console.error(error);
        return;
      }

      if (cart.item_count <= currentRendered) return;

      try {
        const sections = await this.fetchCartSections();
        this.applySectionsFromState({ sections });
      } catch (error) {
        console.error(error);
        return;
      }

      const updatedCount = this.getRenderedItemCount();
      if (updatedCount >= cart.item_count) return;
      currentRendered = updatedCount;
    }
  }

  async refreshSectionsFromServer() {
    const sections = await this.fetchCartSections();
    this.applySectionsFromState({ sections });
  }

  applySectionsFromState(parsedState) {
    if (!parsedState.sections) return;

    // Out-of-order add responses: if a stale response would replace the drawer
    // with fewer items than are already shown, skip apply (sync upgrades later).
    const currentRendered = this.getRenderedItemCount();
    if (currentRendered > 0 && parsedState.sections['cart-drawer']) {
      const responseCount = this.countCartItemsInHTML(parsedState.sections['cart-drawer']);
      if (responseCount < currentRendered) return;
    }

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement || !parsedState.sections[section.id]) return;

      const html = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      if (html) sectionElement.innerHTML = html;
    });
  }

  countCartItemsInHTML(html) {
    if (!html) return 0;
    return new DOMParser().parseFromString(html, 'text/html').querySelectorAll('.cart-item').length;
  }

  fetchCart() {
    return fetch(`${routes.cart_url}.js`, { credentials: 'include' }).then((response) => response.json());
  }

  fetchCartSections() {
    return fetch(`${routes.cart_url}?sections=cart-drawer,cart-icon-bubble`, {
      credentials: 'include',
    }).then((response) => response.json());
  }

  getRenderedItemCount() {
    return document.querySelectorAll('#CartDrawer .cart-item').length;
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (!html) return '';
    const element = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return element ? element.innerHTML : '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
